import { StrictMode, type ComponentType } from 'react';
import { createRoot } from 'react-dom/client';

// Lazy component registry: each entry is a `() => import(...)` factory, so a component's code
// (and its dependencies — notably @coveo/headless in the Search/GlobalSearch* chunks) is only
// downloaded and parsed when a page actually mounts that component. The entry bundle itself
// stays tiny (just this bootstrap + react-dom). webpack emits one async chunk per lazily
// imported module and shares common code (e.g. @coveo/headless) between them.
const componentLoaders: Record<string, () => Promise<{ default: ComponentType<any> }>> = {
  ComponentNotFound: () => import('./components/ComponentNotFound'),
  Search: () => import('./components/Search/Search'),
  ArticleSearch: () => import('./components/Search/layouts/ArticleSearchLayout'),
  GlobalSearchModal: () => import('./components/GlobalSearchModal'),
  // Shared global-search entry point (homepage hero, header modal, 404 page).
  GlobalSearchInput: () => import('./components/GlobalSearchInput'),
};

// `pageshow` (rather than DOMContentLoaded) so this also runs when the page is restored from
// the bfcache. But createRoot() is NOT idempotent — calling it twice on the same element errors
// and leaves a broken/duplicate component (symptom: the header search modal's state triggers but
// it renders empty). So each mounted element is flagged with `data-react-mounted` and skipped on
// any repeat pageshow; the flag survives in the bfcache-restored DOM, and the already-live React
// app simply keeps working.
window.addEventListener('pageshow', () => {
  const elements = document.querySelectorAll('[data-react]:not([data-react-mounted])');

  if (elements.length === 0) return;

  for (const element of elements) {
    const rootElement = element as HTMLElement;

    // Determine which component to load.
    let componentName: string = rootElement.dataset.react ?? '';
    let subComponentName = '';
    if (componentName.includes(':')) {
      const componentNameArray = componentName.split(':');
      componentName = componentNameArray[0];
      subComponentName = componentNameArray[1];
    }

    // Data for the component is dumped onto the page as JSON to hydrate React component
    const elementProps = rootElement.dataset.reactProps;
    let data = {};
    if (elementProps) {
      try {
        data = JSON.parse(elementProps);
      } catch (error) {
        // Leave the element UNFLAGGED so a later pageshow can retry it, and skip it (don't
        // throw) so the other mount points on the page still mount.
        console.error('Error parsing data-react-props:', error);
        continue;
      }
    }

    // Props parsed OK — flag now (still synchronous, before the async import below) so a
    // pageshow that fires again can't double-mount this element. A parse failure above never
    // reaches here, so a bad mount point stays retryable.
    rootElement.dataset.reactMounted = '1';

    const props = {
      componentName,
      subComponentName,
      ...data,
    };

    // Dynamically load only the component this mount point needs, then render it. Unknown
    // names fall back to ComponentNotFound.
    const loadComponent = componentLoaders[componentName] ?? componentLoaders.ComponentNotFound;
    loadComponent()
      .then(({ default: Component }) => {
        const root = createRoot(rootElement);
        root.render(
          <StrictMode>
            <Component {...props} />
          </StrictMode>,
        );
      })
      .catch((error) => {
        // A genuinely failed import shouldn't stay flagged — clear it so a later pageshow can retry.
        delete rootElement.dataset.reactMounted;
        console.error(`Failed to load React component "${componentName}":`, error);
      });
  }
});
